import * as React from 'react';
import { useFormContext, useWatch, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Plus, Minus } from 'lucide-react';
import {
  addDays,
  addMonths,
  addYears,
  endOfWeek,
  isBefore,
  differenceInMonths,
  differenceInYears,
} from 'date-fns';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { DateTimePicker } from '@/components/common/DateTimePicker';
import type { ContractFormValues } from '../schemas';
import type { BillingPeriodType } from '../schemas';

interface BillingPeriodsSectionProps {
  isFieldDisabled: boolean;
  /**
   * Whether the INSTANCE cycle can be picked. Hidden when editing a contract
   * that isn't already instance-based — switching to INSTANCE hard-deletes the
   * contract's billing periods on the backend.
   */
  showInstanceOption?: boolean;
}

// `openEnded` = the contract has no end date: `end` is then only a soft bound
// (today) — weeks keep their natural Sunday end instead of being clamped to it,
// and the week containing the bound is still included.
function collectWeeks(
  start: Date,
  end: Date,
  openEnded = false,
): Array<{ startsAt: Date; endsAt: Date }> {
  const weeks: Array<{ startsAt: Date; endsAt: Date }> = [];
  let current = new Date(start);
  while (openEnded ? !isBefore(end, current) : isBefore(current, end)) {
    const weekEnd = endOfWeek(current, { weekStartsOn: 1 });
    const rangeEnd = !openEnded && isBefore(end, weekEnd) ? end : weekEnd;
    weeks.push({ startsAt: new Date(current), endsAt: new Date(rangeEnd) });
    current = addDays(rangeEnd, 1);
  }
  return weeks;
}

function computeAutoCount(periodType: BillingPeriodType, start: Date, end: Date): number {
  switch (periodType) {
    case 'MONTHLY':
      return Math.max(1, differenceInMonths(end, start));
    case 'YEARLY':
      return Math.max(1, differenceInYears(end, start));
    case 'WEEKLY':
      return Math.max(1, collectWeeks(start, end).length);
    default:
      return 1;
  }
}

// The day after a given date, at local midnight — used to chain each billing
// period's start to the previous period's end (period N+1 starts the day after
// period N ends).
function startOfNextDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1, 0, 0, 0, 0);
}

// Local midnight of a date — used as the End picker's minDate so the calendar
// disables every day before the period's start (a same-day period is allowed).
function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

// `contractEnd` may be null (open-ended contract, no end date). Then periods
// are generated from the contract start up to and INCLUDING the period that
// contains today — the last generated period is the currently-active one, each
// keeping its natural boundary end (never clamped, since there is no contract
// end to clamp to).
function generatePeriods(
  periodType: BillingPeriodType,
  count: number,
  contractStart: Date,
  contractEnd: Date | null,
): Array<{ startsAt: Date; endsAt: Date | null }> {
  const n = Math.max(1, count);
  const ranges: Array<{ startsAt: Date; endsAt: Date | null }> = [];
  const openEnded = !contractEnd;
  // Soft bound for open-ended generation: today, or the contract start when it
  // starts in the future (so exactly one — the first — period is generated).
  const bound = contractEnd ?? (isBefore(new Date(), contractStart) ? contractStart : new Date());

  if (periodType === 'INSTANCE' || periodType === 'MANUAL') {
    return [{ startsAt: contractStart, endsAt: contractEnd }];
  }

  if (periodType === 'WEEKLY') {
    const weeks = collectWeeks(contractStart, bound, openEnded);
    for (let i = 0; i < weeks.length; i += n) {
      const chunk = weeks.slice(i, i + n);
      ranges.push({ startsAt: chunk[0].startsAt, endsAt: chunk[chunk.length - 1].endsAt });
    }
    return ranges;
  }

  if (periodType === 'MONTHLY') {
    let current = contractStart;
    while (openEnded ? !isBefore(bound, current) : isBefore(current, bound)) {
      const nextStart = addMonths(current, n);
      const rangeEnd =
        openEnded || isBefore(nextStart, bound) ? addDays(nextStart, -1) : bound;
      ranges.push({ startsAt: new Date(current), endsAt: new Date(rangeEnd) });
      current = nextStart;
    }
    return ranges;
  }

  if (periodType === 'QUARTER') {
    const quarters = [
      { startMonth: 0, startDay: 1, endMonth: 2, endDay: 31 },
      { startMonth: 3, startDay: 1, endMonth: 5, endDay: 30 },
      { startMonth: 6, startDay: 1, endMonth: 8, endDay: 30 },
      { startMonth: 9, startDay: 1, endMonth: 11, endDay: 31 },
    ];
    const startYear = contractStart.getFullYear();
    const endYear = bound.getFullYear();
    for (let year = startYear; year <= endYear; year++) {
      for (const q of quarters) {
        const qStart = new Date(year, q.startMonth, q.startDay);
        const qEnd = new Date(year, q.endMonth, q.endDay);
        // Closed: quarters starting on/after the contract end are out. Open:
        // include up to the quarter containing today (qStart <= bound).
        const startsTooLate = openEnded ? isBefore(bound, qStart) : !isBefore(qStart, bound);
        if (startsTooLate || isBefore(qEnd, contractStart)) continue;
        const rangeStart = isBefore(qStart, contractStart) ? contractStart : qStart;
        const rangeEnd = !openEnded && isBefore(bound, qEnd) ? bound : qEnd;
        ranges.push({ startsAt: new Date(rangeStart), endsAt: new Date(rangeEnd) });
      }
    }
    return ranges;
  }

  if (periodType === 'SEMI_ANNUAL') {
    const halves = [
      { startMonth: 0, startDay: 1, endMonth: 5, endDay: 30 },
      { startMonth: 6, startDay: 1, endMonth: 11, endDay: 31 },
    ];
    const startYear = contractStart.getFullYear();
    const endYear = bound.getFullYear();
    for (let year = startYear; year <= endYear; year++) {
      for (const h of halves) {
        const hStart = new Date(year, h.startMonth, h.startDay);
        const hEnd = new Date(year, h.endMonth, h.endDay);
        const startsTooLate = openEnded ? isBefore(bound, hStart) : !isBefore(hStart, bound);
        if (startsTooLate || isBefore(hEnd, contractStart)) continue;
        const rangeStart = isBefore(hStart, contractStart) ? contractStart : hStart;
        const rangeEnd = !openEnded && isBefore(bound, hEnd) ? bound : hEnd;
        ranges.push({ startsAt: new Date(rangeStart), endsAt: new Date(rangeEnd) });
      }
    }
    return ranges;
  }

  if (periodType === 'YEARLY') {
    let current = contractStart;
    while (openEnded ? !isBefore(bound, current) : isBefore(current, bound)) {
      const nextStart = addYears(current, n);
      const rangeEnd =
        openEnded || isBefore(nextStart, bound) ? addDays(nextStart, -1) : bound;
      ranges.push({ startsAt: new Date(current), endsAt: new Date(rangeEnd) });
      current = nextStart;
    }
    return ranges;
  }

  return ranges;
}

export function BillingPeriodsSection({
  isFieldDisabled,
  showInstanceOption = true,
}: BillingPeriodsSectionProps) {
  const { t } = useTranslation();
  const form = useFormContext<ContractFormValues>();

  const startsAt = useWatch({ control: form.control, name: 'startsAt' });
  const endsAt = useWatch({ control: form.control, name: 'endsAt' });
  const billingPeriodType = useWatch({ control: form.control, name: 'billingPeriodType' });
  const billingPeriodCount = useWatch({ control: form.control, name: 'billingPeriodCount' });
  // useWatch gives the live form-store value on every render, while `field` from
  // useFieldArray can lag one cycle after replace() — use this for display values.
  const watchedPeriods = useWatch({ control: form.control, name: 'billingPeriods' });

  const { fields, append, remove, replace } = useFieldArray({
    control: form.control,
    name: 'billingPeriods',
  });

  // True when the user has manually changed any billing period date.
  // Resets to false whenever auto-regeneration runs.
  const [isManuallyEdited, setIsManuallyEdited] = React.useState(false);

  // Period ranges should only be auto-regenerated in response to the user
  // changing the cycle type, the count, or the contract dates — never on the
  // initial load (edit mode seeds saved ranges that must be preserved, and the
  // contract's loaded cycle type would otherwise trigger an immediate
  // overwrite). This flips to true on the first such user interaction.
  const allowRegenRef = React.useRef(false);

  const contractStart = React.useMemo(() => {
    if (!startsAt) return null;
    const d = startsAt instanceof Date ? startsAt : new Date(startsAt);
    return isNaN(d.getTime()) ? null : d;
  }, [startsAt]);

  const contractEnd = React.useMemo(() => {
    if (!endsAt) return null;
    const d = endsAt instanceof Date ? endsAt : new Date(endsAt);
    return isNaN(d.getTime()) ? null : d;
  }, [endsAt]);

  const autoCount = React.useMemo(() => {
    if (!billingPeriodType || !contractStart) return undefined;
    // Open-ended contract (no end date): count against today instead.
    if (contractEnd && !isBefore(contractStart, contractEnd)) return undefined;
    const countBound = contractEnd ?? new Date();
    return computeAutoCount(billingPeriodType, contractStart, countBound);
  }, [billingPeriodType, contractStart, contractEnd]);

  // Track whether the user has manually typed into the count input. Once
  // they have, autoCount must not override their value — even if autoCount
  // recomputes from a useWatch re-emit. The flag resets when the cycle
  // type changes so picking a new type still auto-fills the new default.
  const userTypedCountRef = React.useRef(false);
  const prevTypeForCountRef = React.useRef<string | undefined>(undefined);
  React.useEffect(() => {
    if (prevTypeForCountRef.current !== billingPeriodType) {
      prevTypeForCountRef.current = billingPeriodType;
      userTypedCountRef.current = false;
    }
  }, [billingPeriodType]);

  React.useEffect(() => {
    if (autoCount !== undefined && !userTypedCountRef.current) {
      form.setValue('billingPeriodCount', autoCount);
    }
  }, [autoCount]); // eslint-disable-line react-hooks/exhaustive-deps

  // Treat a change to the contract dates (after the initial mount) as a
  // user-driven trigger for regeneration. On mount we only record the baseline
  // so loaded ranges aren't regenerated.
  const dateSignatureRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    const sig = `${contractStart ? contractStart.getTime() : ''}|${contractEnd ? contractEnd.getTime() : ''}`;
    if (dateSignatureRef.current === null) {
      dateSignatureRef.current = sig;
      return;
    }
    if (dateSignatureRef.current !== sig) {
      dateSignatureRef.current = sig;
      allowRegenRef.current = true;
    }
  }, [contractStart, contractEnd]);

  // Regenerate periods and clear manual-edit flag whenever the driving fields change.
  React.useEffect(() => {
    if (!billingPeriodType || !contractStart) return;
    // A contract END date is optional (open-ended contract): with one set the
    // schedule fills the contract span; without one it runs from the start up
    // to (and including) the currently-active period.
    if (contractEnd && !isBefore(contractStart, contractEnd)) return;
    // Skip programmatic/initial-load runs — only regenerate after the user has
    // actively changed the cycle type, count, or contract dates.
    if (!allowRegenRef.current) return;
    const count = billingPeriodCount ?? autoCount ?? 1;
    const generated = generatePeriods(
      billingPeriodType,
      count,
      contractStart,
      contractEnd ?? null,
    );
    replace(generated);
    setIsManuallyEdited(false);
  }, [billingPeriodType, billingPeriodCount, contractStart, contractEnd]); // eslint-disable-line react-hooks/exhaustive-deps

  // Period starts are derived (auto-chained), never hand-picked: period 1 starts
  // at the contract start, every later period starts the day after the previous
  // period's end. The user only sets each period's END date. Submit sends only
  // endDate, so these starts are display/validation only. `ends` is each
  // period's current end date, index-aligned.
  const resyncStarts = React.useCallback(
    (ends: Array<Date | null>) => {
      ends.forEach((_, i) => {
        const start =
          i === 0
            ? contractStart
            : ends[i - 1]
              ? startOfNextDay(ends[i - 1] as Date)
              : null;
        form.setValue(`billingPeriods.${i}.startsAt`, start ?? null);
      });
    },
    [contractStart, form],
  );

  // Set one period's end, then re-chain every start so consecutive periods stay
  // continuous (period N+1 begins the day after period N ends).
  const applyEndChange = React.useCallback(
    (index: number, newEnd: Date | null) => {
      const current = form.getValues('billingPeriods') ?? [];
      const ends = current.map((p, i) =>
        i === index ? newEnd : ((p?.endsAt as Date | null | undefined) ?? null),
      );
      form.setValue(`billingPeriods.${index}.endsAt`, newEnd);
      resyncStarts(ends);
      setIsManuallyEdited(true);
    },
    [form, resyncStarts],
  );

  // Only the contract START date is required to build periods — the end date
  // is optional (open-ended contracts generate periods up to the active one).
  const isDatesRequired = !contractStart;

  const isCountAlwaysDisabled = billingPeriodType === 'YEARLY';
  const isCountDisabled =
    isFieldDisabled ||
    isDatesRequired ||
    isCountAlwaysDisabled ||
    isManuallyEdited ||
    (autoCount !== undefined && autoCount <= 1);

  const isInstance = billingPeriodType === 'INSTANCE';

  const showCount =
    !isInstance &&
    (billingPeriodType === 'WEEKLY' ||
      billingPeriodType === 'MONTHLY' ||
      billingPeriodType === 'YEARLY');

  const countTooltip = isManuallyEdited
    ? t('countOfTypeManualEditTooltip')
    : isCountAlwaysDisabled
      ? t('countOfTypeAutoTooltip')
      : autoCount === 1
        ? t('countOfTypeOnlyOneTooltip')
        : null;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={isDatesRequired ? 'cursor-not-allowed' : undefined}>
            <div className={`space-y-4 ${isDatesRequired ? 'pointer-events-none opacity-50' : ''}`}>
              <div className="grid gap-4 md:grid-cols-2">
                {/* Period type select */}
                <FormField
                  name="billingPeriodType"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('billingPeriod')} *</FormLabel>
                      <FormControl>
                        <Select
                          disabled={isFieldDisabled || isDatesRequired}
                          value={field.value ?? ''}
                          onValueChange={(v) => {
                            allowRegenRef.current = true;
                            field.onChange(v);
                          }}
                        >
                          <SelectTrigger className="bg-white">
                            <SelectValue placeholder={t('selectBillingPeriod')} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="WEEKLY">{t('weekly')}</SelectItem>
                            <SelectItem value="MONTHLY">{t('monthly')}</SelectItem>
                            <SelectItem value="QUARTER">{t('quarter')}</SelectItem>
                            <SelectItem value="SEMI_ANNUAL">{t('semiAnnual')}</SelectItem>
                            <SelectItem value="YEARLY">{t('yearly')}</SelectItem>
                            {showInstanceOption && (
                              <SelectItem value="INSTANCE">{t('billingInstance')}</SelectItem>
                            )}
                            <SelectItem value="MANUAL">{t('manual')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Count of type */}
                {showCount && (
                  <FormField
                    name="billingPeriodCount"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('countOfType')}</FormLabel>
                        <FormControl>
                          {isCountDisabled && countTooltip ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="cursor-not-allowed">
                                  <Input
                                    type="number"
                                    min={1}
                                    disabled
                                    className="bg-white pointer-events-none"
                                    value={field.value ?? ''}
                                    readOnly
                                  />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>{countTooltip}</TooltipContent>
                            </Tooltip>
                          ) : (
                            <Input
                              type="number"
                              min={1}
                              disabled={isCountDisabled}
                              className="bg-white"
                              value={field.value ?? ''}
                              onChange={(e) => {
                                const val = e.target.value;
                                userTypedCountRef.current = true;
                                allowRegenRef.current = true;
                                field.onChange(val === '' ? undefined : Number(val));
                              }}
                            />
                          )}
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>

              {/* Per-period start + end date pickers */}
              {!isInstance && fields.length > 0 && (
                <div className="space-y-3">
                  <p className="text-xs text-muted-foreground">{t('billingPeriodAutoStartHint')}</p>
                  {fields.map((field, index) => {
                    const periodStart =
                      (watchedPeriods?.[index]?.startsAt as Date | null | undefined) ?? undefined;
                    const periodEnd =
                      (watchedPeriods?.[index]?.endsAt as Date | null | undefined) ?? undefined;
                    return (
                      <div
                        key={field.id}
                        className="space-y-2 rounded-md border border-input/60 bg-slate-50/40 p-3"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">
                            {t('billingPeriodRange')} {index + 1}
                          </span>
                          {!isFieldDisabled && fields.length > 1 && (
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-7 w-7 shrink-0"
                              onClick={() => {
                                const remaining = (form.getValues('billingPeriods') ?? []).filter(
                                  (_, i) => i !== index,
                                );
                                remove(index);
                                // Re-chain the starts of the periods that shifted up.
                                resyncStarts(
                                  remaining.map(
                                    (p) => (p?.endsAt as Date | null | undefined) ?? null,
                                  ),
                                );
                                setIsManuallyEdited(true);
                              }}
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {/* Start — auto-chained (read-only): contract start for the
                              first period, the day after the previous period's end after. */}
                          <FormItem>
                            <FormLabel className="text-xs text-muted-foreground">
                              {t('startDate')}
                            </FormLabel>
                            <FormControl>
                              <DateTimePicker
                                mode="date"
                                value={periodStart}
                                onChange={() => {}}
                                disabled
                              />
                            </FormControl>
                          </FormItem>
                          {/* End — the only date the user picks; dates before this
                              period's start are disabled. Changing it re-chains the
                              next period's start. */}
                          <FormItem>
                            <FormLabel className="text-xs text-muted-foreground">
                              {t('endDate')}
                            </FormLabel>
                            <FormControl>
                              <DateTimePicker
                                mode="date"
                                value={periodEnd}
                                minDate={periodStart ? startOfDay(periodStart) : undefined}
                                onChange={(date) => applyEndChange(index, date ?? null)}
                                disabled={isFieldDisabled || isDatesRequired || !periodStart}
                              />
                            </FormControl>
                          </FormItem>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Billing periods validation error */}
              <FormField
                name="billingPeriods"
                control={form.control}
                render={() => (
                  <FormItem>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Add period button */}
              {!isInstance && !isFieldDisabled && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isDatesRequired}
                  onClick={() => {
                    // New period starts the day after the last period's end
                    // (or at the contract start when it's the first one).
                    const current = form.getValues('billingPeriods') ?? [];
                    const lastEnd =
                      current.length > 0
                        ? ((current[current.length - 1]?.endsAt as
                            | Date
                            | null
                            | undefined) ?? null)
                        : null;
                    const newStart =
                      current.length === 0
                        ? contractStart
                        : lastEnd
                          ? startOfNextDay(lastEnd)
                          : null;
                    append({ startsAt: newStart ?? null, endsAt: null });
                    setIsManuallyEdited(true);
                  }}
                  className="gap-2"
                >
                  <Plus className="h-4 w-4" />
                  {t('addBillingPeriod')}
                </Button>
              )}
            </div>
          </div>
        </TooltipTrigger>
        {isDatesRequired && (
          <TooltipContent>{t('billingPeriodsRequiresDates')}</TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
}
